import { useContext, useMemo, createContext } from "react";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { buildStringFilter } from "services/gaDimensionsService";
import * as customDimensions from "constants/customDimensions";
import { useReportContext } from "components/ReportContext/ReportContextProvider";
import { DIMENSION_DATE, DIMENSION_EVENT_NAME } from "constants/dimensions";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import * as customEvents from "constants/customEvents";
import * as contentCategories from "constants/contentCategories";
import * as ftuCategories from "constants/ftuCategories";
import { SOURCE_PLUGIT } from "constants/contentSources";

const UserEngagementAnalyticsContext = createContext({
  reportRequests: null,
});

const UserEngagementAnalyticsContextProvider = ({ children }) => {
  const { id: domain } = useParams();
  const { dateStrings } = useReportContext();

  const dateRanges = useMemo(() => {
    if (!dateStrings || !dateStrings[0] || !dateStrings[1]) return [];
    return [
      {
        startDate: dateStrings[0],
        endDate: dateStrings[1],
      },
    ];
  }, [dateStrings]);

  const reportRequests = useMemo(() => {
    if (isEmpty(dateRanges)) {
      return null;
    }

    return [
      // 0: User Activation (by date and category) - REPORT_INDEX_ACTIVATION
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: customDimensions.DIMENSION_ITEM_CATEGORY2,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_COMPLETE_ITEM
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                contentCategories.CATEGORY_USER_ACTIVATION
              ),
            ],
          },
        },
      },
      // 1: User signup (by date) - REPORT_INDEX_USER_SIGNUP
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: customDimensions.DIMENSION_ITEM_CATEGORY2,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_COMPLETE_ITEM
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                contentCategories.CATEGORY_USER_REGISTRATION
              ),
            ],
          },
        },
      },
      // 2: Tooltip open - REPORT_INDEX_TOOLTIP_OPEN
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY2,
                ftuCategories.CATEGORY_TOOLTIP_V1
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_OPEN_ITEM
              ),
            ],
          },
        },
      },
      // 3: Merken button clicks (by date) - REPORT_INDEX_MERKEN_CLICKS
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY2,
                ftuCategories.CATEGORY_TOOLTIP_V1
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_VIEW_ITEM_CLICKED
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                ftuCategories.CATEGORY_MERKEN_BUTTON
              ),
            ],
          },
        },
      },

      // 4: Merken button impressions by variant id - REPORT_INDEX_MERKEN_IMPRESSIONS
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: customDimensions.DIMENSION_ITEM_CATEGORY2, // variantId
          },
          {
            name: customDimensions.DIMENSION_ITEM_SOURCE_STREAM_CATEGORY, // zoneId
          },
          {
            name: customDimensions.DIMENSION_PLATFORM, // platform
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_VIEW_ITEM_IMPRESSION
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                contentCategories.CATEGORY_MERKEN_BUTTON
              ),
            ],
          },
        },
      },
      // 5: Merken button clicks by variant id - REPORT_INDEX_MERKEN_CLICKS_BY_VARIANT
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: customDimensions.DIMENSION_ITEM_CATEGORY2, // variantId
          },
          {
            name: customDimensions.DIMENSION_ITEM_SOURCE_STREAM_CATEGORY, // zoneId
          },
          {
            name: customDimensions.DIMENSION_PLATFORM, // platform
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_VIEW_ITEM_CLICKED
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                contentCategories.CATEGORY_MERKEN_BUTTON
              ),
            ],
          },
        },
      },
      // 6: Wizard step impressions (by date and step number) - REPORT_INDEX_WIZARD_STEP_IMPRESSIONS
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: customDimensions.DIMENSION_STEP_NUMBER,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                ftuCategories.CATEGORY_ONBOARDING_V1
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_VIEW_ITEM_DETAILS
              ),
            ],
          },
        },
      },
      // 7: Wizard dismissals (by date) - REPORT_INDEX_WIZARD_DISMISSALS
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                ftuCategories.CATEGORY_ONBOARDING_V1
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_CLOSE_ITEM
              ),
            ],
          },
        },
      },
      // 8: Wizard register button clicks (by date) - REPORT_INDEX_WIZARD_REGISTER_CLICKS
      {
        dateRanges,
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              buildStringFilter(customDimensions.DIMENSION_ITEM_BRAND, domain),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_SOURCE,
                SOURCE_PLUGIT
              ),
              buildStringFilter(
                customDimensions.DIMENSION_ITEM_CATEGORY,
                ftuCategories.SIGNUP_BUTTON
              ),
              buildStringFilter(
                DIMENSION_EVENT_NAME,
                customEvents.EVENT_VIEW_ITEM_CLICKED
              ),
            ],
          },
        },
      },
    ];
  }, [dateRanges, domain]);

  return (
    <UserEngagementAnalyticsContext.Provider
      value={{
        reportRequests,
      }}
    >
      {children}
    </UserEngagementAnalyticsContext.Provider>
  );
};

export const useUserEngagementAnalyticsContext = () =>
  useContext(UserEngagementAnalyticsContext);

export default UserEngagementAnalyticsContextProvider;
